import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress,
    Input, Select, Option, IconButton, Modal, ModalDialog,
    ModalClose, Divider, Avatar, Tooltip,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import AddRoundedIcon           from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon        from '@mui/icons-material/DeleteRounded';
import SearchRoundedIcon        from '@mui/icons-material/SearchRounded';
import ReceiptLongRoundedIcon   from '@mui/icons-material/ReceiptLongRounded';
import FilterListRoundedIcon    from '@mui/icons-material/FilterListRounded';
import ArrowBackRoundedIcon     from '@mui/icons-material/ArrowBackRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import CreditCardRoundedIcon    from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon      from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon   from '@mui/icons-material/PointOfSaleRounded';

import DateQuickPicker from '../../components/DateQuickPicker';
import UndoToast from '../../components/UndoToast';
import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetOrders, APIDeleteOrder,
    APIGetEmployees, APIGetLocations,
    parseApiError,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';

// ─── Утилиты ──────────────────────────────────────────────────────────────────
const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const getMonthStart = () => {
    const now = new Date();
    return toISO(new Date(now.getFullYear(), now.getMonth(), 1));
};

const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
};

const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

const getInitials = (name = '') => {
    const parts = name.trim().split(' ');
    return (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
};

const MONEY_ICONS = {
    transfer: <CreditCardRoundedIcon sx={{ fontSize: 14 }} />,
    cash:     <PaymentsRoundedIcon   sx={{ fontSize: 14 }} />,
    terminal: <PointOfSaleRoundedIcon sx={{ fontSize: 14 }} />,
};

const MONEY_LABELS = {
    transfer: 'Перевод',
    cash:     'Наличные',
    terminal: 'Терминал',
};

const MONEY_COLORS = {
    transfer: { color: '#818cf8', bg: 'rgba(129,140,248,0.10)' },
    cash:     { color: '#22c55e', bg: 'rgba(34,197,94,0.10)' },
    terminal: { color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = {
    hidden:  {},
    visible: { transition: { staggerChildren: 0.04 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};

const inputSx = {
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    fontSize: 13, color: 'white', flex: 1,
    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
    "&:hover": {
        bgcolor: 'var(--joy-palette-background-level3)', 
    }
};


// ════════════════════════════════════════════════════════════════════
//  OrdersPage
// ════════════════════════════════════════════════════════════════════
export default function OrdersPage() {
    const { token, showToast, onRefreshTick, hasPerm } = useContext(AppContext);
    const navigate = useNavigate();

    const canCreate = hasPerm('finance.orders.create');
    const canDelete = hasPerm('finance.orders.delete');

    // ── Данные ─────────────────────────────────────────────────────
    const [orders,        setOrders]        = useState([]);
    const [employees,     setEmployees]     = useState([]);
    const [locations,     setLocations]     = useState([]);
    const [loading,       setLoading]       = useState(true);
    const [undoData,      setUndoData]           = useState(null); // { ids: [1,2,3], message: '3 чека добавлено' }

    // ── Фильтры ───────────────────────────────────────────────────
    const [dateFrom,      setDateFrom]      = useState(getMonthStart);
    const [dateTo,        setDateTo]        = useState(() => toISO(new Date()));
    const [filterPhoto,   setFilterPhoto]   = useState('');
    const [filterLoc,     setFilterLoc]     = useState('');
    const [filterMoney,   setFilterMoney]   = useState('');
    const [search,        setSearch]        = useState('');
    const [showFilters,   setShowFilters]   = useState(false);
    const [activeQuick, setActiveQuick] = useState('');

    // ── Удаление ──────────────────────────────────────────────────
    const [deleteTarget,  setDeleteTarget]  = useState(null);
    const [deleting,      setDeleting]      = useState(false);

    // ── Загрузка ──────────────────────────────────────────────────
    const loadOrders = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo)   params.date_to   = dateTo;
        if (filterPhoto) params.photographer_id = filterPhoto;
        if (filterLoc)   params.location_id     = filterLoc;

        const data = await APIGetOrders(token, params);
        if (Array.isArray(data)) setOrders(data);
        setLoading(false);
    }, [token, dateFrom, dateTo, filterPhoto, filterLoc]);

    const loadMeta = useCallback(async () => {
        const [empl, locs] = await Promise.all([
            APIGetEmployees(token, 'photographer'),
            APIGetLocations(token),
        ]);
        if (Array.isArray(empl)) setEmployees(empl);
        if (Array.isArray(locs)) setLocations(locs);
    }, [token]);

    useEffect(() => { loadMeta(); }, [loadMeta]);
    useEffect(() => { loadOrders(); }, [loadOrders]);
    useEffect(() => onRefreshTick(loadOrders), [onRefreshTick, loadOrders]);

    // ── Удаление ──────────────────────────────────────────────────
    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        const res = await APIDeleteOrder(token, deleteTarget.id);
        if (res?.error) {
            showToast(parseApiError(res.error), 'danger');
        } else {
            showToast('Чек удалён');
            setOrders(prev => prev.filter(o => o.id !== deleteTarget.id));
        }
        setDeleting(false);
        setDeleteTarget(null);
    };

    // ── Фильтрация + поиск на клиенте ────────────────────────────
    const filtered = useMemo(() => {
        let list = orders;
        if (filterMoney) list = list.filter(o => o.money_type === filterMoney);
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(o =>
                (o.photographer_name || '').toLowerCase().includes(q) ||
                (o.location_name || '').toLowerCase().includes(q) ||
                String(o.value).includes(q)
            );
        }
        return list;
    }, [orders, filterMoney, search]);

    // ── Итоги ─────────────────────────────────────────────────────
    const totals = useMemo(() => {
        const transfer = filtered.filter(o => o.money_type === 'transfer').reduce((s, o) => s + parseFloat(o.value || 0), 0);
        const cash     = filtered.filter(o => o.money_type === 'cash').reduce((s, o) => s + parseFloat(o.value || 0), 0);
        const terminal = filtered.filter(o => o.money_type === 'terminal').reduce((s, o) => s + parseFloat(o.value || 0), 0);
        return { transfer, cash, terminal, total: transfer + cash + terminal, count: filtered.length };
    }, [filtered]);

    // ── Группировка по дате ───────────────────────────────────────
    const grouped = useMemo(() => {
        const map = {};
        const sorted = [...filtered].sort((a, b) => (b.date || '').localeCompare(a.date || '') || b.id - a.id);
        for (const o of sorted) {
            const key = o.date || 'unknown';
            if (!map[key]) map[key] = [];
            map[key].push(o);
        }
        return Object.entries(map);
    }, [filtered]);

    return (
        <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Шапка ──────────────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <IconButton variant="plain" onClick={() => navigate('/manager')}
                                sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                                <ArrowBackRoundedIcon />
                            </IconButton>
                            <Box>
                                <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                    Чеки
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                    Управление продажами фотографов
                                </Typography>
                            </Box>
                        </Box>
                        {canCreate && (
                            <Button
                                startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => navigate('/manager/orders/add')}
                                sx={{
                                    background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                    color: '#fff', fontWeight: 700, fontSize: 13, border: 'none',
                                    boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                    '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                                }}
                            >
                                Добавить чек
                            </Button>
                        )}
                    </Box>
                </motion.div>

                {/* ── Итого-карточки ────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.08 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' }, gap: 1.5, mb: 2.5 }}>
                        {[
                            { label: 'Всего',     value: fmtMoney(totals.total),    sub: `${totals.count} чек.`, color: 'var(--joy-palette-primary-400)', bg: 'rgba(162,38,226,0.08)', icon: <AccountBalanceWalletRoundedIcon sx={{ fontSize: 18 }} /> },
                            { label: 'Перевод',   value: fmtMoney(totals.transfer), color: '#818cf8', bg: 'rgba(129,140,248,0.08)', icon: <CreditCardRoundedIcon sx={{ fontSize: 18 }} /> },
                            { label: 'Наличные',  value: fmtMoney(totals.cash),     color: '#22c55e', bg: 'rgba(34,197,94,0.08)', icon: <PaymentsRoundedIcon sx={{ fontSize: 18 }} /> },
                            { label: 'Терминал',  value: fmtMoney(totals.terminal), color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', icon: <PointOfSaleRoundedIcon sx={{ fontSize: 18 }} /> },
                        ].map(s => (
                            <Box key={s.label} sx={{ px: 2, py: 1.8, borderRadius: 'var(--joy-radius-lg)', border: '1px solid var(--joy-palette-divider)', bgcolor: s.bg }}>
                                <Box sx={{ color: s.color, mb: 0.5 }}>{s.icon}</Box>
                                <Typography sx={{ fontSize: 22, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</Typography>
                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.4 }}>{s.label}</Typography>
                                {s.sub && <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>{s.sub}</Typography>}
                            </Box>
                        ))}
                    </Box>
                </motion.div>

                {/* ── Фильтры ──────────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.12 }}>
                    <Box sx={{
                        bgcolor: 'var(--joy-palette-background-level1)',
                        border: '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                        p: { xs: 1.5, sm: 2 }, mb: 2.5,
                    }}>
                        {/* Строка поиска + кнопка фильтров */}
                        <Box sx={{ display: 'flex', gap: 1, mb: showFilters ? 1.5 : 0 }}>
                            <Input
                                placeholder="Поиск по имени, точке, сумме…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                startDecorator={<SearchRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-text-tertiary)' }} />}
                                sx={inputSx}
                                size="sm"
                            />
                            <IconButton
                                variant={showFilters ? 'solid' : 'outlined'}
                                color={showFilters ? 'primary' : 'neutral'}
                                onClick={() => setShowFilters(p => !p)}
                                size="sm"
                                sx={{ 
                                    bgcolor: 'var(--joy-palette-background-level2)', 
                                    border: '1px solid var(--joy-palette-divider)',
                                    "&:hover": {
                                        bgcolor: 'var(--joy-palette-background-level3)', 
                                    }
                                }}
                            >
                                <FilterListRoundedIcon sx={{ fontSize: 18, color: 'white' }} />
                            </IconButton>
                        </Box>
                        
                        {/* Быстрые периоды */}
                        <Stack direction={"row"} sx={{ flexWrap: 'wrap', gap: 0.8, mb: 1.5, mt: 2 }}>
                            {[
                                { label: 'Сегодня', from: toISO(new Date()), to: toISO(new Date()) },
                                { label: 'Вчера',   from: (() => { const d = new Date(); d.setDate(d.getDate()-1); return toISO(d); })(), to: (() => { const d = new Date(); d.setDate(d.getDate()-1); return toISO(d); })() },
                                { label: 'Неделя',  from: (() => { const d = new Date(); d.setDate(d.getDate() - (d.getDay() === 0 ? 6 : d.getDay()-1)); return toISO(d); })(), to: toISO(new Date()) },
                                { label: 'Месяц',   from: getMonthStart(), to: toISO(new Date()) },
                            ].map(p => (
                                <Chip key={p.label} size="sm" variant="outlined" color="neutral"
                                    onClick={() => { setDateFrom(p.from); setDateTo(p.to); setShowFilters(true); }}
                                    sx={{ cursor: 'pointer', fontWeight: 700, fontSize: 12, px: 2, py: 0.5, 
                                        ...inputSx, 
                                        "&:hover": {
                                            color: 'black'
                                        }
                                    }}
                                >
                                    {p.label}
                                </Chip>
                            ))}
                        </Stack>

                        {/* Развёрнутые фильтры */}
                        <AnimatePresence>
                            {showFilters && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.25 }}
                                    style={{ overflow: 'hidden' }}
                                >
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr 1fr' }, gap: 1 }}>
                                        {/* <Box sx={{ mb: 1.5 }}>
                                            <DateQuickPicker
                                                activeKey={activeQuick}
                                                onChange={(from, to, key) => {
                                                    setActiveQuick(key);
                                                    setDateFrom(from);
                                                    setDateTo(to);
                                                }}
                                            />
                                        </Box> */}
                                        <Input type="date" size="sm" value={dateFrom}
                                            onChange={(e) => { setDateFrom(e.target.value); setActiveQuick(''); }}
                                            slotProps={{ input: { 'aria-label': 'Дата от' } }}
                                            sx={inputSx}
                                        />
                                        <Input type="date" size="sm" value={dateTo}
                                            onChange={(e) => { setDateFrom(e.target.value); setActiveQuick(''); }}
                                            slotProps={{ input: { 'aria-label': 'Дата до' } }}
                                            sx={inputSx}
                                        />
                                        <Select size="sm" value={filterPhoto} onChange={(_, v) => setFilterPhoto(v || '')}
                                            placeholder="Фотограф"
                                            sx={inputSx}
                                            slotProps={{
                                                listbox: {
                                                    sx: {
                                                        bgcolor: 'var(--joy-palette-background-level2)',
                                                        border: '1px solid var(--joy-palette-divider)',
                                                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                                        '& .MuiOption-root': {
                                                            color: 'var(--joy-palette-text-primary)',
                                                            fontSize: 13,
                                                            '&:hover': {
                                                                bgcolor: 'rgba(162,38,226,0.12)',
                                                                color: 'var(--joy-palette-text-primary)',
                                                            },
                                                            '&[aria-selected="true"]': {
                                                                bgcolor: 'rgba(162,38,226,0.2)',
                                                                color: 'var(--joy-palette-primary-300)',
                                                                fontWeight: 600,
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            <Option value="">Все фотографы</Option>
                                            {employees.map(e => (
                                                <Option key={e.id} value={e.id}>{e.first_name} {e.last_name}</Option>
                                            ))}
                                        </Select>
                                        <Select size="sm" value={filterLoc} onChange={(_, v) => setFilterLoc(v || '')}
                                            placeholder="Точка"
                                            sx={inputSx}
                                            slotProps={{
                                                listbox: {
                                                    sx: {
                                                        bgcolor: 'var(--joy-palette-background-level2)',
                                                        border: '1px solid var(--joy-palette-divider)',
                                                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                                        '& .MuiOption-root': {
                                                            color: 'var(--joy-palette-text-primary)',
                                                            fontSize: 13,
                                                            '&:hover': {
                                                                bgcolor: 'rgba(162,38,226,0.12)',
                                                                color: 'var(--joy-palette-text-primary)',
                                                            },
                                                            '&[aria-selected="true"]': {
                                                                bgcolor: 'rgba(162,38,226,0.2)',
                                                                color: 'var(--joy-palette-primary-300)',
                                                                fontWeight: 600,
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            <Option value="">Все точки</Option>
                                            {locations.map(l => (
                                                <Option key={l.id} value={l.id}>{l.name}</Option>
                                            ))}
                                        </Select>
                                        <Select size="sm" value={filterMoney} onChange={(_, v) => setFilterMoney(v || '')}
                                            placeholder="Тип оплаты"
                                            sx={inputSx}
                                            slotProps={{
                                                listbox: {
                                                    sx: {
                                                        bgcolor: 'var(--joy-palette-background-level2)',
                                                        border: '1px solid var(--joy-palette-divider)',
                                                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                                        '& .MuiOption-root': {
                                                            color: 'var(--joy-palette-text-primary)',
                                                            fontSize: 13,
                                                            '&:hover': {
                                                                bgcolor: 'rgba(162,38,226,0.12)',
                                                                color: 'var(--joy-palette-text-primary)',
                                                            },
                                                            '&[aria-selected="true"]': {
                                                                bgcolor: 'rgba(162,38,226,0.2)',
                                                                color: 'var(--joy-palette-primary-300)',
                                                                fontWeight: 600,
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            <Option value="">Все типы</Option>
                                            <Option value="transfer">Перевод</Option>
                                            <Option value="cash">Наличные</Option>
                                            <Option value="terminal">Терминал</Option>
                                        </Select>
                                    </Box>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Box>
                </motion.div>

                {/* ── Список чеков ──────────────────────────────── */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : filtered.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <ReceiptLongRoundedIcon sx={{ fontSize: 48, color: 'var(--joy-palette-text-tertiary)', mb: 1.5 }} />
                        <Typography sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }}>
                            Нет чеков за выбранный период
                        </Typography>
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={2}>
                            {grouped.map(([date, items]) => (
                                <motion.div key={date} variants={fadeUp}>
                                    <Box sx={{
                                        bgcolor: 'var(--joy-palette-background-level1)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        borderRadius: 'var(--joy-radius-xl)',
                                        overflow: 'hidden',
                                    }}>
                                        {/* Заголовок дня */}
                                        <Box sx={{
                                            px: 2, py: 1.2,
                                            borderBottom: '1px solid var(--joy-palette-divider)',
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                {fmtDate(date)}
                                            </Typography>
                                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                                                {items.length} чек. · {fmtMoney(items.reduce((s, o) => s + parseFloat(o.value || 0), 0))} ₽
                                            </Typography>
                                        </Box>

                                        {/* Чеки за день */}
                                        <Stack spacing={0}>
                                            {items.map((order, idx) => {
                                                const mc = MONEY_COLORS[order.money_type] || MONEY_COLORS.transfer;
                                                return (
                                                    <Box key={order.id} sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                                        px: 2, py: 1.2,
                                                        borderBottom: idx < items.length - 1
                                                            ? '1px solid var(--joy-palette-divider)' : 'none',
                                                        transition: 'background .15s',
                                                        '&:hover': { bgcolor: 'var(--joy-palette-background-level2)' },
                                                    }}>
                                                        {/* Аватар фотографа */}
                                                        <Avatar size="sm" sx={{
                                                            width: 32, height: 32, fontSize: 11, fontWeight: 700,
                                                            bgcolor: 'rgba(162,38,226,0.15)',
                                                            color: 'var(--joy-palette-primary-300)',
                                                            border: '1.5px solid var(--joy-palette-primary-500)',
                                                            flexShrink: 0,
                                                        }}>
                                                            {getInitials(order.photographer_name)}
                                                        </Avatar>

                                                        {/* Инфо */}
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography sx={{
                                                                fontSize: 13, fontWeight: 600,
                                                                color: 'var(--joy-palette-text-primary)',
                                                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                                            }}>
                                                                {order.photographer_name}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.3 }}>
                                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                                    {order.location_name}
                                                                </Typography>
                                                                {order.administrator_name && (
                                                                    <>
                                                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>·</Typography>
                                                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                                            Админ: {order.administrator_name}
                                                                        </Typography>
                                                                    </>
                                                                )}
                                                            </Box>
                                                            {order.created_by_name && (
                                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                                                    Внёс: {order.created_by_name} · {new Date(order.date_create).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                                </Typography>
                                                            )}
                                                        </Box>

                                                        {/* Тип оплаты */}
                                                        <Chip size="sm" variant="soft" sx={{
                                                            fontSize: 10, fontWeight: 700,
                                                            bgcolor: mc.bg, color: mc.color,
                                                            flexShrink: 0,
                                                        }}
                                                            startDecorator={MONEY_ICONS[order.money_type]}
                                                        >
                                                            {MONEY_LABELS[order.money_type]}
                                                        </Chip>

                                                        {/* Сумма */}
                                                        <Typography sx={{
                                                            fontSize: 15, fontWeight: 800,
                                                            color: 'var(--joy-palette-text-primary)',
                                                            minWidth: 60, textAlign: 'right',
                                                            fontVariantNumeric: 'tabular-nums',
                                                        }}>
                                                            {fmtMoney(order.value)}
                                                        </Typography>

                                                        {/* Удалить */}
                                                        {canDelete && (
                                                            <Tooltip title="Удалить чек" size="sm">
                                                                <IconButton
                                                                    size="sm" variant="plain"
                                                                    onClick={() => setDeleteTarget(order)}
                                                                    sx={{
                                                                        color: 'var(--joy-palette-text-tertiary)',
                                                                        '&:hover': { color: 'var(--joy-palette-danger-400)' },
                                                                        flexShrink: 0,
                                                                    }}
                                                                >
                                                                    <DeleteRoundedIcon sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                </motion.div>
                            ))}
                        </Stack>
                    </motion.div>
                )}
            </Box>

            {/* ── Модалка удаления ───────────────────────────────── */}
            <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
                <ModalDialog sx={{
                    bgcolor: 'var(--joy-palette-background-surface)',
                    border: '1px solid var(--joy-palette-divider)',
                    maxWidth: 380,
                }}>
                    <ModalClose />
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'var(--joy-palette-text-primary)', mb: 1 }}>
                        Удалить чек?
                    </Typography>
                    {deleteTarget && (
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 2 }}>
                            {deleteTarget.photographer_name} — {fmtMoney(deleteTarget.value)} ₽ ({MONEY_LABELS[deleteTarget.money_type]})
                            за {fmtDate(deleteTarget.date)}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button variant="outlined" color="neutral" size="sm"
                            onClick={() => setDeleteTarget(null)}>
                            Отмена
                        </Button>
                        <Button color="danger" size="sm" loading={deleting}
                            onClick={handleDelete}>
                            Удалить
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </motion.div>
    );
}